/**
 * The imports client — spec §N, T-6.8.
 *
 * Two things about the shape are worth knowing before reading a screen that
 * uses it.
 *
 * **Nothing here imports anything.** `upload` stores a file and queues a dry
 * run; `commit` queues the commit. Both come back immediately with a job whose
 * `status` is `queued`, and the work happens in the worker — so every screen
 * that shows a job has to poll, and `isRunning` below is what it polls on.
 *
 * **The vocabulary comes from the service.** The six kinds, their columns and
 * the guidance beside each one are `GET /imports/vocabulary`, which is rendered
 * from the same registry the parser reads. A second list of columns in
 * TypeScript would disagree with the parser by the end of the month, and the
 * one the firm can see would be the wrong one.
 */

import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import { SERVICE_API } from '../config'
import { session } from '../session'

export type ImportKind =
  | 'clients'
  | 'cases'
  | 'invoices'
  | 'documents'
  | 'calendar'
  | 'tags'

export type ImportStatus = 'queued' | 'running' | 'previewed' | 'committed' | 'failed'
export type ImportMode = 'dry_run' | 'commit'
export type RowStatus = 'ready' | 'created' | 'skipped' | 'rejected'

export interface ImportKindInfo {
  kind: ImportKind
  label: string
  columns: string[]
  required: string[]
  accepts: string[]
  /** What a person needs to know before filling the file in. */
  guidance: string
}

export interface ImportJob {
  id: string
  kind: ImportKind
  mode: ImportMode
  status: ImportStatus
  filename: string
  content_type: string
  size_bytes: number
  total_rows: number
  ready_rows: number
  created_rows: number
  skipped_rows: number
  rejected_rows: number
  error_code: string | null
  error: string | null
  /** What each plan cap answered, recorded whether or not it refused. */
  caps: Record<string, { allowed: boolean; reason: string }>
  started_at: string | null
  finished_at: string | null
  created_at: string | null
  started_by: string | null
  /** The service's answer, not the screen's — see `app/api/imports.py`. */
  can_commit: boolean
}

export interface ImportRow {
  id: string
  row_no: number
  status: RowStatus
  source_ref: string | null
  reason_code: string | null
  reason: string | null
  /** What this row would do, in the firm's words. The preview *is* this. */
  summary: string | null
  target_id: string | null
  result: Record<string, unknown>
}

/** A job the worker has not finished with. The screen polls while this is true. */
export function isRunning(job: ImportJob): boolean {
  return job.status === 'queued' || job.status === 'running'
}

export const imports = {
  vocabulary: () => api<ImportKindInfo[]>('/api/v1/imports/vocabulary'),
  list: (kind?: ImportKind) =>
    api<ImportJob[]>(`/api/v1/imports${kind ? `?kind=${kind}` : ''}`),
  read: (id: string) => api<ImportJob>(`/api/v1/imports/${id}`),
  rows: (id: string, status?: RowStatus) =>
    api<ImportRow[]>(`/api/v1/imports/${id}/rows${status ? `?status=${status}` : ''}`),
  commit: (id: string) => api<ImportJob>(`/api/v1/imports/${id}/commit`, { method: 'POST' }),
}

/**
 * Upload one file — multipart, so not through `api()`.
 *
 * The header is set by hand for the reason `lib/api.ts` documents at length:
 * `@fiveox/shell`'s `authed` spreads what it is given, and a `Headers`
 * instance spreads to `{}`. And `content-type` is **deliberately absent** — the
 * browser sets it, with the multipart boundary, and setting it here would send
 * a boundary-less type the server cannot parse.
 */
export async function upload(kind: ImportKind, file: File): Promise<ImportJob> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)

  const form = new FormData()
  form.append('kind', kind)
  form.append('file', file)

  const res = await session.authed(`${SERVICE_API}/api/v1/imports`, {
    method: 'POST',
    headers: { [ORG_HEADER]: orgId },
    body: form,
  })
  const body = (await res.json().catch(() => null)) as
    | { success: boolean; data: ImportJob; error?: { code: string; message: string } }
    | null
  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'That file could not be uploaded.',
      res.status,
    )
  }
  return body.data
}

/**
 * A file, fetched **authenticated** — the template and the reject report.
 *
 * A plain `<a href>` cannot do it: both routes are behind a bearer and the org
 * header, and a browser navigation carries neither, so the link would download a
 * 401 named like a CSV. The shape `reports.downloadCsv` uses, for the same
 * reason.
 */
async function fetchCsv(path: string, whenItFails: string): Promise<Blob> {
  const orgId = getOrgId()
  if (!orgId) throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
  const res = await session.authed(`${SERVICE_API}${path}`, {
    headers: { [ORG_HEADER]: orgId },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null
    throw new ApiError(body?.error?.code ?? 'INTERNAL', body?.error?.message ?? whenItFails, res.status)
  }
  return res.blob()
}

export const downloadTemplate = (kind: ImportKind) =>
  fetchCsv(`/api/v1/imports/templates/${kind}.csv`, 'That template could not be downloaded.')

export const downloadRejects = (id: string) =>
  fetchCsv(`/api/v1/imports/${id}/rejects.csv`, 'That report could not be downloaded.')

/** Hand a fetched blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
